/**
 * Predictive Search Web Component
 * High-performance search with live debounced suggestions
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.clearBtn = this.querySelector('[data-clear-search]');
    this.abortController = null;

    if (!this.input) return;

    this.input.addEventListener('input', debounce((e) => this.onChange(e), 300));
    this.input.addEventListener('focus', () => this.onFocus());

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.close();
        this.input.focus();
      });
    }

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (searchTerm.length) {
      this.open();
    }
  }

  getSearchResults(searchTerm) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setAttribute('loading', 'true');

    const searchUrl = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page,article&resources[options][unavailable_products]=last&resources[options][fields]=title,body,vendor,product_type,tag&section_id=predictive-search`;

    fetch(searchUrl, { signal: this.abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.resultsContainer.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Predictive Search Error:', error);
        }
      })
      .finally(() => {
        this.removeAttribute('loading');
      });
  }

  open() {
    this.setAttribute('open', 'true');
    this.resultsContainer.removeAttribute('hidden');
    if (this.clearBtn) this.clearBtn.style.display = 'flex';
  }

  close() {
    this.removeAttribute('open');
    this.resultsContainer.setAttribute('hidden', 'true');
    if (this.clearBtn && !this.getQuery().length) this.clearBtn.style.display = 'none';
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
